import React from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  styled,
  Stack,
  Typography,
} from '@mui/material';
import { Button } from 'components/Button/Button';
import { CloseButton } from 'components/CloseButton/CloseButton';
import { SvgIcon } from 'components/SvgIcon/SvgIcon';
import { useLayoutElements } from 'contexts/LayoutElementsContext/useLayoutElements';
import { ReactComponent as RestrictedAccess } from 'assets/images/AccessRestriction/RestrictedAccess.svg';

interface AccessRestrictionDialogButtonProps {
  label: string;
  onClick: () => void;
}

export interface AccessRestrictionDialogProps {
  open: boolean;
  title: string;
  body: string;
  primaryCtaButton: AccessRestrictionDialogButtonProps;
  onClose: () => void;
}

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'normal',
  gap: theme.spacing(2),
  padding: theme.spacing(4),
}));

const StyledDialogContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2),
}));

const StyledCloseButton = styled(CloseButton)(({ theme }) => ({
  color: theme.palette.grey[500],
  alignSelf: 'flex-end',
}));

const StyledSvgIcon = styled(SvgIcon)(() => ({
  width: 43,
  height: 52,
}));

export const AccessRestrictionDialog = ({
  open,
  title,
  body,
  primaryCtaButton,
  onClose,
}: AccessRestrictionDialogProps) => {
  const { isAlertOpen } = useLayoutElements();

  return (
    <Dialog
      className="access-restriction-dialog"
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="access-restriction-dialog__title"
      aria-describedby="access-restriction-dialog__body"
      disableEscapeKeyDown={isAlertOpen} // Prevent closing if an alert is open
      disableEnforceFocus // Allow TalkBack to navigate freely
      PaperProps={{
        tabIndex: 0,
        sx: {
          width: '328px',
          height: '394px',
        },
      }}
    >
      <DialogContent sx={{ padding: 0 }}>
        <StyledDialogContentContainer>
          <StyledCloseButton
            className="access-restriction-dialog__close-button"
            onClick={onClose}
            aria-label="Close access restriction dialog"
          />
          <Stack spacing={3} alignItems="center" textAlign="center" paddingTop={3}>
            <StyledSvgIcon
              className="access-restriction-dialog__icon"
              component={RestrictedAccess}
              inheritViewBox
              aria-hidden="true"
            />
            <Typography
              className="access-restriction-dialog__title"
              variant="title3"
              color="primary"
              id="access-restriction-dialog__title"
            >
              {title}
            </Typography>
            <Typography
              className="access-restriction-dialog__body"
              variant="body1"
              id="access-restriction-dialog__body"
            >
              {body}
            </Typography>
          </Stack>
        </StyledDialogContentContainer>
      </DialogContent>
      <StyledDialogActions disableSpacing>
        <Button
          className="access-restriction-dialog__primary-button"
          variant="primary"
          onClick={primaryCtaButton.onClick}
        >
          {primaryCtaButton.label}
        </Button>
      </StyledDialogActions>
    </Dialog>
  );
};
